import { useState } from 'react';
import { Box, Container, Typography, Button, Grid, Paper, Link, Menu, MenuItem, Chip } from '@mui/material';
import { Email } from '@mui/icons-material';
import DashboardAnimation from '../components/DashboardAnimation';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import { useNavigate } from 'react-router-dom';
import { QRCode } from '../components/QRCode';
import BrandMark from '../components/BrandMark';

const Index = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const loginMenuOpen = Boolean(anchorEl);

  const handleLoginMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const consultationScreenshots = [
    { src: '/screenshots/initial-consultation-workflow.png', alt: 'Initial consultation workflow board' },
    { src: '/screenshots/initial-consultation-appraisal-terms.png', alt: 'AI appraisal report commission and terms' },
    { src: '/screenshots/initial-consultation-access-code.png', alt: 'Vendor access code gate' },
    { src: '/screenshots/initial-consultation-approval.png', alt: 'Vendor approval options' },
  ];

  const openHouseScreenshots = [
    { src: '/screenshots/openly-main-page.png', alt: 'Openly main page' },
    { src: '/screenshots/open-house-visitor-list.png', alt: 'Visitor registering at open house' },
    { src: '/screenshots/qr-checkin-mobile.png', alt: 'QR self check-in on mobile' },
  ];

  const sectionSpacing = { my: { xs: 8, md: 10 } };

  const cardSx = {
    p: 3,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'grey.200',
    bgcolor: '#fff',
    boxShadow: '0 10px 30px rgba(15,23,42,0.06)',
    transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 18px 40px rgba(15,23,42,0.14)',
      borderColor: 'primary.light',
    },
  };

  const panelSx = {
    p: { xs: 4, md: 6 },
    borderRadius: 4,
    border: '1px solid',
    borderColor: 'grey.200',
    background: 'linear-gradient(135deg, #f6f9ff 0%, #ffffff 55%, #f2f7ff 100%)',
    boxShadow: '0 24px 80px rgba(15,23,42,0.12)',
  };

  const accentChipSx = {
    bgcolor: 'primary.light',
    color: 'primary.dark',
    fontWeight: 700,
    letterSpacing: 0.2,
    borderRadius: '999px',
    height: 28,
    px: 1.5,
  };

  const whyReasons = [
    {
      title: '🧩 Everything in One Place',
      body: 'Manage properties, open homes, contractors, and tasks in one workspace.',
    },
    {
      title: '⚡ Automate Your Workflow',
      body: 'Replace manual admin with smart automation — never miss a step again.',
    },
    {
      title: '📈 Impress Vendors with Real Reporting',
      body: 'Generate beautiful AI-powered vendor reports instantly.',
    },
    {
      title: '📱 Mobile-Friendly for Agents',
      body: 'Run inspections, tasks, and workflows from your phone.',
    },
    {
      title: '👥 Built for Collaboration',
      body: 'Principals, admins, and agents stay aligned on every listing.',
    },
    {
      title: '🔒 Secure & Compliant',
      body: 'Digital records, visitor logs, and audit trails built for Australian real estate.',
    },
  ];

  const demoHighlights = [
    {
      title: 'Property Workflow View',
      body: 'Visualise every step in your property lifecycle, from appraisal to settlement.',
    },
    {
      title: 'Calendar Assistant',
      body: 'Open scheduling for consultations and opens, with automated reminders.',
    },
    {
      title: 'Contractor Task Management',
      body: 'Assign, track, and complete prep jobs (staging, media, repairs) with visibility.',
    },
    {
      title: 'Open House Attendee Capture',
      body: 'Capture every visitor with QR sign-ins and instant lead creation.',
    },
    {
      title: 'AI Vendor Report',
      body: 'Generate beautifully formatted, AI-powered vendor reports in seconds.',
    },
    {
      title: 'Market Campaign',
      body: 'Send targeted email and SMS campaigns to visitors based on property, budget, and preferences.',
    },
    {
      title: 'Commission Report',
      body: 'Track pipeline and paid commission by agent, with timeline and calendar views.',
    },
    {
      title: 'Sales / Listing Report',
      body: 'Track sales performance and listing activity with reports and analytics.',
    },
  ];

  const coreFeatures = [
    {
      title: '🏠 Property Lifecycle Management',
      body: 'From onboarding to settlement — every step is defined, assigned, and tracked.',
    },
    {
      title: '📅 Task & Deadline Automation',
      body: 'Automated scheduling, reminders, follow-ups, and compliance tasks.',
    },
    {
      title: '📧 Market Campaign',
      body: 'Send targeted email and SMS campaigns to visitors based on property, budget, and preferences.',
    },
    {
      title: '🤝 Contractor Portal',
      body: 'Assign work orders, track progress, and manage invoices effortlessly.',
    },
    {
      title: '📲 Open House Attendee App',
      body: 'QR code sign-ins, instant lead capture, follow-up workflows, and visitor analytics.',
    },
    {
      title: '🔗 Domain & RealEstate.com.au Sync',
      body: 'Seamless listing updates and property data sync.',
    },
    {
      title: '📊 Reporting & Analytics',
      body: 'Performance dashboards, trends, vendor reports, and open home summaries.',
    },
    {
      title: '⚙ Custom Workflows',
      body: 'Build your own templates for presale, inspections, auctions, and sales.',
    },
    {
      title: '👥 Team Collaboration',
      body: 'Manage team members, assign roles, and collaborate seamlessly across properties and tasks.',
    },
    {
      title: '📆 Smart Calendar',
      body: 'Advanced scheduling with automated reminders, conflict detection, and event management.',
    },
    {
      title: '🤖 AI-Powered Reports',
      body: 'Generate beautifully formatted vendor reports and analytics with AI in seconds.',
    },
    {
      title: '✨ AI-Generated Property Description',
      body: 'Create compelling property descriptions automatically using AI, saving time while maintaining quality.',
    },
  ];

  const scrollToMobileApp = () => {
    document.getElementById('mobile-app')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BrandMark size={28} />
          <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', letterSpacing: '-0.02em', color: 'text.primary' }}>
            Workflow.Estate
          </Typography>
        </Box>

        {/* Action Buttons - Top Right */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="text"
            size="medium"
            onClick={scrollToMobileApp}
            sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500 }}
          >
            Download Openly App
          </Button>
        <Button
          variant="outlined"
          onClick={handleLoginMenu}
          aria-controls={loginMenuOpen ? 'login-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={loginMenuOpen ? 'true' : undefined}
        >
          Sign in
        </Button>
        <Menu
          id="login-menu"
          anchorEl={anchorEl}
          open={loginMenuOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/login');
            }}
          >
            Agent Sign in
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/contractor/auth');
            }}
          >
            Contractor Sign in
          </MenuItem>
        </Menu>
        </Box>
      </Box>
      {/* HERO SECTION */}
      <Box
        sx={{
          ...panelSx,
          my: { xs: 6, md: 8 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          gap: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Chip label="Agency-ready automation" size="small" sx={accentChipSx} />
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2.4rem', md: '3.2rem' },
              lineHeight: 1.1,
            }}
          >
            Run every property workflow in one platform
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.7,
              maxWidth: 620,
            }}
          >
            Workflow Estate helps agencies manage listings, inspections, vendors, contractors, and
            reporting — with zero admin and full automation.
          </Typography>
          <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                px: 3,
                boxShadow: '0 18px 40px rgba(33, 150, 243, 0.25)',
                '&:hover': { boxShadow: '0 20px 50px rgba(33, 150, 243, 0.32)' },
              }}
            >
              Register now
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/demo')}
              sx={{
                px: 3,
                borderRadius: '999px',
                '&:hover': { borderColor: 'primary.main', color: 'primary.main', transform: 'translateY(-2px)' },
                transition: 'transform 160ms ease, border-color 160ms ease',
              }}
            >
              Watch Demo
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={scrollToMobileApp}
              sx={{
                px: 3,
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'grey.300',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'grey.50',
                  transform: 'translateY(-2px)',
                },
                transition: 'transform 160ms ease, border-color 160ms ease, background-color 160ms ease',
              }}
            >
              Download Openly App
            </Button>
          </Box>
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: 'text.secondary',
            }}
          >
            Hurry up — register now and enjoy 2 months of Agency Premium plan free. No credit card, no commitment · Built for modern real estate teams · Works on desktop &amp; mobile
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 260,
            borderRadius: 4,
            bgcolor: 'rgba(255,255,255,0.8)',
            border: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 3 },
            boxShadow: '0 16px 50px rgba(15,23,42,0.12)',
          }}
        >
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <DashboardAnimation />
          </Box>
        </Box>
      </Box>

      {/* WHY SECTION */}
      <Box id="why" sx={sectionSpacing}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800 }}>
          Why real estate teams choose Workflow Estate
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 740, mx: 'auto', mt: 1.5 }}>
          Cleaner workflows, happier vendors, and less admin time. These are the wins teams tell us about most.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 4 }}>
          {whyReasons.map((reason) => (
            <Grid item xs={12} sm={6} md={4} key={reason.title}>
              <Paper variant="outlined" sx={cardSx}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {reason.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {reason.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* PRODUCT DEMO / SCREENSHOTS SECTION */}
      <Box id="product-demo" sx={sectionSpacing}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800 }}>
          See how Workflow Estate works
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto', mt: 1.5 }}>
          A quick peek at the core screens teams use every day.
        </Typography>
        <Box sx={{ ...panelSx, mt: 4 }}>
          <Box sx={{ maxWidth: 1000, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <ScreenshotCarousel />
            <Grid container spacing={3}>
              {demoHighlights.map((highlight) => (
                <Grid item xs={12} sm={6} md={3} key={highlight.title}>
                  <Paper variant="outlined" sx={{ ...cardSx, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {highlight.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {highlight.body}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* INITIAL CONSULTATION & MARKET APPRAISAL */}
      <Box id="initial-consultation" sx={sectionSpacing}>
        <Box sx={panelSx}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                Initial consultation with AI market appraisal
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Capture the property brief, generate an AI-powered appraisal report, and send it
                securely to vendors with clear approval responses.
              </Typography>
              <Grid container spacing={1.2} sx={{ mt: 2 }}>
                {[
                  'Market appraisal generated with pricing guidance and marketing/staging fees',
                  'Branded PDF delivered with a secure 6-digit access code gate',
                  'Vendors can Accept, Decline, or mark Need to Discuss right inside the viewer',
                  'Responses sync back to the workflow so the team can move to agreement and marketing'
                ].map((item) => (
                  <Grid item xs={12} key={item}>
                    <Typography variant="body2" color="text.secondary">• {item}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid item xs={12} md={7}>
              <ScreenshotCarousel screenshots={consultationScreenshots} />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* CORE FEATURES SECTION */}
      <Box id="features" sx={sectionSpacing}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800 }}>
          Everything you need to run a modern agency
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto', mt: 1.5 }}>
          A streamlined toolkit that keeps your team, vendors, and contractors moving in sync.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 4 }}>
          {coreFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Paper variant="outlined" sx={{ ...cardSx, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {feature.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* DEDICATED OPEN HOUSE MODULE SECTION */}
      <Box id="open-house" sx={sectionSpacing}>
        <Box sx={{ ...panelSx, background: 'linear-gradient(135deg, #f3f7ff 0%, #ffffff 60%, #f7fbff 100%)' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                Capture every open house visitor — automatically
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Never lose a lead again. Workflow Estate's Open House Attendee App gives you a
                seamless sign-in experience with QR codes, instant lead creation, and automated
                follow-up tasks.
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 3 }}>
                {['QR code check-in', 'Digital visitor registration', 'Auto-create contacts', 'Follow-up workflows', 'Visitor analytics', 'Exportable logs'].map((item) => (
                  <Grid item xs={6} key={item}>
                    <Typography variant="body2" color="text.secondary">
                      • {item}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="text"
                  size="large"
                  onClick={() => navigate('/attendees')}
                  sx={{ textTransform: 'none', fontWeight: 700, '&:hover': { transform: 'translateX(4px)' }, transition: 'transform 150ms ease' }}
                >
                  See how it works &rarr;
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <ScreenshotCarousel screenshots={openHouseScreenshots} />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* MOBILE APP DOWNLOAD SECTION */}
      <Box id="mobile-app" sx={sectionSpacing}>
        <Box sx={{ ...panelSx, background: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 50%, #f1f8f4 100%)' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 800 }}>
                Download Openly Mobile App
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Get the Openly mobile app to capture and manage open house attendees on the go. 
                Record attendee details quickly at inspections, view and manage visitor lists, 
                send market campaigns, and generate self check-in QR codes.
              </Typography>
              <Grid container spacing={1.5} sx={{ mt: 3 }}>
                {['Record attendee details quickly', 'View and manage visitor lists', 'Generate self check-in QR codes', 'Send market campaigns', 'Work faster on the go'].map((item) => (
                  <Grid item xs={12} sm={6} key={item}>
                    <Typography variant="body2" color="text.secondary">
                      ✓ {item}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  href="https://apps.apple.com/au/app/openly-by-workflow-estate/id6757623810"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    bgcolor: '#000',
                    '&:hover': { bgcolor: '#333', transform: 'translateY(-2px)' }, 
                    transition: 'transform 150ms ease, background-color 150ms ease',
                    boxShadow: '0 4px 14px 0 rgba(0,0,0,0.25)'
                  }}
                >
                  Download on App Store →
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  href="https://play.google.com/store/apps/details?id=estate.workflow.openly"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 700,
                    px: 3,
                    py: 1.5,
                    '&:hover': { transform: 'translateY(-2px)' }, 
                    transition: 'transform 150ms ease',
                    boxShadow: '0 4px 14px 0 rgba(76, 175, 80, 0.39)'
                  }}
                >
                  Download on Google Play →
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box sx={{ 
                p: 3, 
                bgcolor: 'white', 
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(15,23,42,0.08)',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3
              }}>
                {/* Apple Store QR - first */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    App Store (iPhone)
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'grey.200'
                  }}>
                    <QRCode
                      value="https://apps.apple.com/au/app/openly-by-workflow-estate/id6757623810"
                      size={180}
                      level="M"
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 160 }}>
                    Scan with iPhone camera
                  </Typography>
                </Box>
                {/* Google Play QR - second */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    Google Play (Android)
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'white', 
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'grey.200'
                  }}>
                    <QRCode
                      value="https://play.google.com/store/apps/details?id=estate.workflow.openly"
                      size={180}
                      level="M"
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 160 }}>
                    Scan with Android camera
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>


      {/* SOCIAL PROOF SECTION */}
      <Box id="social-proof" sx={sectionSpacing}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800 }}>
          Loved by forward-thinking real estate teams
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto', mt: 1.5 }}>
          Built alongside agencies to remove admin noise and make every interaction faster.
        </Typography>
        <Grid container spacing={3} sx={{ mt: 3 }}>
          {[
            '“One place for the whole property journey—appraisals, tasks, documents, and follow-ups, so we spend less time on admin and more time with clients.”',
            '“Built with Australian agencies so it fits how we actually work, not the other way around.”',
            '“From first appraisal to settlement, everything stays organised, visible, and in control.”',
          ].map((quote) => (
            <Grid item xs={12} md={4} key={quote}>
              <Paper variant="outlined" sx={{ ...cardSx, height: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                  {quote}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          mt: 8,
          mb: 10,
          pt: 4,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          pr: { xs: 0, sm: '80px' }, // Add padding on right to account for chat button
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CodePulse Pty Ltd
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Contact:{' '}
            <Link href="mailto:support@workflow.estate" underline="hover">
              support@workflow.estate
            </Link>
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: { xs: 'flex-start', sm: 'flex-end' },
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Link href="#features" color="inherit" underline="hover" sx={{ fontSize: '0.875rem' }}>
              Features
            </Link>
            <Link
              href="/support"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem' }}
            >
              Support
            </Link>
            <Link
              href="/contact-sales"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem' }}
            >
              Contact
            </Link>
            <Link
              href="/privacy"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem' }}
            >
              Privacy Policy
            </Link>
             <Link
              href="/login"
              color="inherit"
              underline="hover"
              sx={{ fontSize: '0.875rem' }}
            >
              Log In
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default Index; 